import React, { useEffect, useState } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth } from '../contexts/AuthContext';
import { useTheme } from '../contexts/ThemeContext';
import { getCourse, enrollInCourse, validateCoupon } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import PaymentButton, { PaymentSummary } from '../components/PaymentButton';
import axios from 'axios';
import {
  ArrowLeft, Check, Shield, Award, Clock, BookOpen,
  GraduationCap, IndianRupee, Percent, Sparkles, CreditCard,
  Lock, CheckCircle, AlertCircle
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function Enrollment() {
  const { courseId } = useParams();
  const [searchParams] = useSearchParams();
  const tier = searchParams.get('tier') || 'self_learning';
  const couponFromUrl = searchParams.get('coupon') || '';
  
  const { user, isAuthenticated } = useAuth();
  const { theme } = useTheme();
  const navigate = useNavigate();
  
  const [course, setCourse] = useState(null);
  const [pricingTiers, setPricingTiers] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [enrollment, setEnrollment] = useState(null);
  const [couponCode, setCouponCode] = useState(couponFromUrl);
  const [appliedCoupon, setAppliedCoupon] = useState(null);
  const [validatingCoupon, setValidatingCoupon] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: `/enroll/${courseId}?tier=${tier}` } });
      return;
    }
    loadData();
  }, [courseId, isAuthenticated]);

  // Auto-apply coupon from URL
  useEffect(() => {
    if (couponFromUrl && pricingTiers && !appliedCoupon) {
      handleValidateCoupon();
    }
  }, [couponFromUrl, pricingTiers]);

  const loadData = async () => {
    try {
      const [courseRes, pricingRes] = await Promise.all([
        getCourse(courseId),
        axios.get(`${API}/courses/${courseId}/pricing`)
      ]);
      setCourse(courseRes.data);
      setPricingTiers(pricingRes.data.pricing_tiers);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load course information');
    } finally {
      setLoading(false);
    }
  };

  const handleValidateCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }

    const selectedTier = tier === 'instructor_led' ? pricingTiers?.instructor_led : pricingTiers?.self_learning;
    if (!selectedTier) return;

    setValidatingCoupon(true);
    try {
      const response = await validateCoupon(couponCode, courseId, tier, selectedTier.price);
      setAppliedCoupon(response.data);
      toast.success(response.data.message);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
      setAppliedCoupon(null);
    } finally {
      setValidatingCoupon(false);
    }
  };

  const handleCreateEnrollment = async () => {
    setEnrolling(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await axios.post(
        `${API}/enrollments`,
        {
          course_id: courseId,
          tier_type: tier,
          coupon_code: appliedCoupon?.code || null
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setEnrollment(response.data);
      toast.success('Enrollment created! Please complete payment.');
    } catch (error) {
      if (error.response?.data?.detail?.includes('already enrolled')) {
        toast.info('You are already enrolled in this course');
        navigate('/dashboard');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to create enrollment');
      }
    } finally {
      setEnrolling(false);
    }
  };

  const handlePaymentSuccess = (paymentData) => {
    setPaymentComplete(true);
    setTimeout(() => {
      navigate('/dashboard');
    }, 2000);
  };

  const handlePaymentError = (error) => {
    console.error('Payment error:', error);
  };

  const cardClass = theme === 'dark' ? 'card-shadow-dark' : 'card-shadow-light';

  const selectedTier = tier === 'instructor_led' ? pricingTiers?.instructor_led : pricingTiers?.self_learning;
  const originalPrice = selectedTier?.price || 0;
  const discountAmount = appliedCoupon?.discount_amount || 0;
  const finalPrice = appliedCoupon?.final_price || originalPrice;

  if (loading) {
    return (
      <div className="min-h-screen pt-20 pb-12">
        <div className="max-w-4xl mx-auto px-4">
          <Skeleton className="h-8 w-48 mb-6" />
          <div className="grid md:grid-cols-2 gap-8">
            <Skeleton className="h-[400px] rounded-2xl" />
            <Skeleton className="h-[400px] rounded-2xl" />
          </div>
        </div>
      </div>
    );
  }

  if (!course || !selectedTier) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold mb-4">Course or pricing not found</h1>
          <Button onClick={() => navigate('/courses')}>Browse Courses</Button>
        </div>
      </div>
    );
  }

  if (paymentComplete) {
    return (
      <div className="min-h-screen pt-20 pb-12 flex items-center justify-center">
        <motion.div
          initial={{ scale: 0.8, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          className="text-center"
        >
          <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-green-500/20 flex items-center justify-center">
            <CheckCircle className="w-10 h-10 text-green-500" />
          </div>
          <h1 className="text-3xl font-bold mb-2">Payment Successful!</h1>
          <p className="text-muted-foreground mb-6">
            Welcome to {course.title}. Redirecting to your dashboard...
          </p>
          <div className="animate-spin w-6 h-6 border-2 border-primary border-t-transparent rounded-full mx-auto" />
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-20 pb-12" data-testid="enrollment-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Back Button */}
        <Button
          variant="ghost"
          onClick={() => navigate(`/courses/${courseId}`)}
          className="mb-6"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Course
        </Button>

        {/* Header */}
        <div className="mb-8">
          <Badge variant="outline" className="mb-3">
            <Lock className="w-3 h-3 mr-1" />
            Secure Checkout
          </Badge>
          <h1 className="text-3xl font-bold mb-2">Complete Your Enrollment</h1>
          <p className="text-muted-foreground">
            You're one step away from starting your creative journey!
          </p>
        </div>

        <div className="grid md:grid-cols-5 gap-8">
          {/* Course & Plan Summary - Left Column */}
          <div className="md:col-span-3 space-y-6">
            {/* Course Card */}
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg">Course Details</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex gap-4">
                  <img
                    src={course.thumbnail_url || 'https://images.unsplash.com/photo-1618005182384-a83a8bd57fbe?w=200'}
                    alt={course.title}
                    className="w-24 h-24 rounded-lg object-cover"
                  />
                  <div>
                    <h3 className="font-semibold text-lg">{course.title}</h3>
                    <p className="text-sm text-muted-foreground line-clamp-2">{course.description}</p>
                    <div className="flex items-center gap-4 mt-2 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="w-4 h-4" />
                        {course.duration}
                      </span>
                      <span className="flex items-center gap-1">
                        <BookOpen className="w-4 h-4" />
                        {course.modules?.length || 0} Modules
                      </span>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Selected Plan Card */}
            <Card className={`${cardClass} ${tier === 'instructor_led' ? 'border-emerald-500/50' : ''}`}>
              <CardHeader className="pb-2">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-2">
                    {tier === 'instructor_led' ? (
                      <GraduationCap className="w-5 h-5 text-emerald-500" />
                    ) : (
                      <BookOpen className="w-5 h-5 text-blue-500" />
                    )}
                    {selectedTier.name}
                  </CardTitle>
                  {tier === 'instructor_led' && (
                    <Badge className="bg-emerald-500/10 text-emerald-600">Recommended</Badge>
                  )}
                </div>
              </CardHeader>
              <CardContent>
                <p className="text-sm text-muted-foreground mb-4">{selectedTier.description}</p>
                <div className="grid grid-cols-2 gap-2">
                  {selectedTier.features?.filter(f => f.included).slice(0, 6).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 text-sm">
                      <Check className="w-4 h-4 text-emerald-500" />
                      <span>{feature.name}</span>
                    </div>
                  ))}
                </div>
              </CardContent>
            </Card>

            {/* Coupon Section */}
            <Card className={cardClass}>
              <CardHeader className="pb-2">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Percent className="w-5 h-5" />
                  Have a Coupon?
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-2">
                  <Input
                    placeholder="Enter coupon code"
                    value={couponCode}
                    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                    disabled={!!appliedCoupon}
                    className="flex-1"
                  />
                  {appliedCoupon ? (
                    <Button
                      variant="outline"
                      onClick={() => {
                        setAppliedCoupon(null);
                        setCouponCode('');
                      }}
                    >
                      Remove
                    </Button>
                  ) : (
                    <Button
                      onClick={handleValidateCoupon}
                      disabled={validatingCoupon || !couponCode.trim()}
                    >
                      {validatingCoupon ? 'Validating...' : 'Apply'}
                    </Button>
                  )}
                </div>
                {appliedCoupon && (
                  <div className="mt-3 p-3 bg-green-500/10 rounded-lg flex items-center gap-2 text-green-600">
                    <CheckCircle className="w-4 h-4" />
                    <span className="text-sm">
                      Coupon "{appliedCoupon.code}" applied! You save ₹{discountAmount.toLocaleString('en-IN')}
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          {/* Payment Summary - Right Column */}
          <div className="md:col-span-2">
            <Card className={`${cardClass} sticky top-24`}>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <CreditCard className="w-5 h-5" />
                  Order Summary
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-6">
                {/* Price Breakdown */}
                <PaymentSummary
                  originalAmount={originalPrice}
                  discountAmount={discountAmount}
                  couponCode={appliedCoupon?.code}
                  tier={tier}
                />

                {/* Payment Actions */}
                {!enrollment ? (
                  <Button
                    className="w-full h-12 text-base font-semibold"
                    onClick={handleCreateEnrollment}
                    disabled={enrolling}
                  >
                    {enrolling ? (
                      <>
                        <div className="w-4 h-4 mr-2 border-2 border-white border-t-transparent rounded-full animate-spin" />
                        Creating Enrollment...
                      </>
                    ) : (
                      <>
                        Proceed to Payment
                        <ArrowLeft className="w-4 h-4 ml-2 rotate-180" />
                      </>
                    )}
                  </Button>
                ) : (
                  <PaymentButton
                    amount={finalPrice}
                    courseId={courseId}
                    tier={tier}
                    enrollmentId={enrollment.enrollment_id}
                    couponCode={appliedCoupon?.code}
                    onSuccess={handlePaymentSuccess}
                    onError={handlePaymentError}
                    buttonText={`Pay ₹${finalPrice.toLocaleString('en-IN')}`}
                    className="w-full h-12 text-base font-semibold bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600"
                    user={user}
                  />
                )}

                {/* Trust Badges */}
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <Shield className="w-3.5 h-3.5" />
                    Secure Payment
                  </span>
                  <span className="flex items-center gap-1">
                    <Award className="w-3.5 h-3.5" />
                    7-Day Refund
                  </span>
                </div>

                {/* Payment Methods */}
                <div className="border-t pt-4">
                  <p className="text-xs text-muted-foreground text-center mb-2">Accepted Payment Methods</p>
                  <div className="flex justify-center gap-4 text-muted-foreground">
                    <span className="text-xs">UPI</span>
                    <span className="text-xs">Cards</span>
                    <span className="text-xs">Net Banking</span>
                    <span className="text-xs">Wallets</span>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
